import React from 'react';
import PublicNav from '../../components/layout/PublicNav';
import PublicFooter from '../../components/layout/PublicFooter';
import PageMeta from '../../components/seo/PageMeta';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <>
      <PageMeta
        title="Terms of Service | TRITAL Care®"
        description="Terms of Service for TRITAL Care workforce platform."
        path="/terms"
      />
      
      <PublicNav />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          {/* Header */}
          <div className="mb-12">
            <Link to="/" className="text-sm text-brand-navy hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="font-display font-black text-4xl lg:text-5xl text-slate-900 tracking-tight">
              Terms of Service
            </h1>
            <p className="mt-3 text-slate-600">
              Last updated: May 2026
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
              <p className="text-sm text-slate-700 mb-0">
                <strong>TRITAL Care®</strong> is the operating brand name of <strong>TOPTAL Care Inc.</strong>, 
                a Texas corporation. These Terms of Service govern your use of the TRITAL Care platform, 
                including all websites, mobile applications, and services.
              </p>
            </div>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                By accessing or using TRITAL Care services, you agree to be bound by these Terms of Service 
                and our Privacy Policy. If you do not agree to these terms, you may not use our services.
              </p>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify these terms at any time. Continued use of our services 
                following any changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">2. Account Registration</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Maintain the security of your password</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Immediately notify us of any unauthorized use</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">3. SMS Communications</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                <strong>By providing your mobile number and opting in to SMS communications, you agree to receive:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
                <li>Job opportunity notifications</li>
                <li>Shift availability alerts</li>
                <li>Account notifications and updates</li>
                <li>Compliance reminders and credential expiry alerts</li>
                <li>Related staffing and platform communications</li>
              </ul>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-4">
                <p className="text-sm text-amber-900 mb-2">
                  <strong>Important SMS Disclosures:</strong>
                </p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-amber-900 mb-0">
                  <li>Message frequency varies based on your activity and open shifts</li>
                  <li>Message and data rates may apply from your carrier</li>
                  <li>Reply <strong>STOP</strong> to opt out at any time</li>
                  <li>Reply <strong>HELP</strong> for assistance</li>
                  <li>Consent to receive SMS is <strong>NOT</strong> a condition of purchase, employment, or platform access</li>
                  <li>Carriers are not liable for delayed or undelivered messages</li>
                </ul>
              </div>

              <p className="text-slate-700 leading-relaxed mb-2">
                <strong>Opt-Out:</strong> To stop receiving text messages, text STOP to any message from TRITAL Care, 
                or update your preferences in your account settings. You may also email us at{' '}
                <a href="mailto:support@tritalcare.com" className="text-brand-navy hover:underline">
                  support@tritalcare.com
                </a>.
              </p>
              
              <p className="text-slate-700 leading-relaxed">
                <strong>Support:</strong> For help, text HELP to any message from TRITAL Care, 
                call +1 (726) 701-2273, or email{' '}
                <a href="mailto:support@tritalcare.com" className="text-brand-navy hover:underline">
                  support@tritalcare.com
                </a>.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">4. User Conduct</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Use the platform for any illegal purpose</li>
                <li>Provide false, inaccurate, or misleading information</li>
                <li>Impersonate any person or entity</li>
                <li>Interfere with or disrupt the platform</li>
                <li>Attempt to gain unauthorized access</li>
                <li>Submit false credentials or qualifications</li>
                <li>Harass, abuse, or harm other users</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">5. Clinician Users</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you are a healthcare clinician using our platform, you additionally agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Maintain all required licenses and certifications</li>
                <li>Provide accurate competency and skill information</li>
                <li>Comply with all facility policies and procedures</li>
                <li>Maintain professional conduct standards</li>
                <li>Notify us immediately of any license suspensions or restrictions</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">6. Facility Users</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you represent a healthcare facility, you additionally agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Provide accurate facility information and requirements</li>
                <li>Comply with all employment and labor laws</li>
                <li>Maintain appropriate credentialing standards</li>
                <li>Provide a safe and compliant work environment</li>
                <li>Process timely payments for services rendered</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">7. Payment Terms</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                For paid services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Fees are non-refundable unless otherwise stated</li>
                <li>We reserve the right to change fees with notice</li>
                <li>You are responsible for all applicable taxes</li>
                <li>Late payments may result in service suspension</li>
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">8. Intellectual Property</h2>
              <p className="text-slate-700 leading-relaxed">
                All platform content, features, and functionality are owned by TOPTAL Care Inc. and are 
                protected by United States and international copyright, trademark, and other intellectual 
                property laws. You may not reproduce, distribute, or create derivative works without our 
                express written permission.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">9. Privacy and Data</h2>
              <p className="text-slate-700 leading-relaxed">
                Your use of our services is also governed by our{' '}
                <Link to="/privacy-policy" className="text-brand-navy hover:underline font-medium">
                  Privacy Policy
                </Link>
                , which describes how we collect, use, and protect your personal information.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">10. Disclaimers</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, 
                EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
                AND NON-INFRINGEMENT.
              </p>
              <p className="text-slate-700 leading-relaxed">
                We do not warrant that the platform will be uninterrupted, secure, or error-free. We do not 
                guarantee the accuracy or completeness of any information on the platform.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">11. Limitation of Liability</h2>
              <p className="text-slate-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, TOPTAL CARE INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE PLATFORM.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">12. Governing Law</h2>
              <p className="text-slate-700 leading-relaxed">
                These Terms shall be governed by the laws of the State of Texas, without regard to its conflict 
                of law provisions. Any disputes shall be resolved in the state or federal courts located in 
                Harris County, Texas.
              </p>
            </section>

            <section className="mb-10">
              <h2 className="font-display font-bold text-2xl text-slate-900 mb-4">13. Contact Us</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                For questions about these Terms of Service, contact us:
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                <p className="text-slate-700 mb-2">
                  <strong>TOPTAL Care Inc. (d/b/a TRITAL Care®)</strong>
                </p>
                <p className="text-slate-700 mb-1">
                  5423 Pecan Fall PL<br />
                  Missouri City, TX 77459
                </p>
                <p className="text-slate-700 mb-1">
                  Phone: <a href="tel:+17267012273" className="text-brand-navy hover:underline">+1 (726) 701-2273</a>
                </p>
                <p className="text-slate-700 mb-0">
                  Email: <a href="mailto:info@tritalcare.com" className="text-brand-navy hover:underline">info@tritalcare.com</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </>
  );
}
